import { useCallback, useEffect, useRef } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { DEFAULT_PAGE_SIZE } from '../constants/partner-data-gateway.constant';
import { FileProcessingStatus } from '../domain/enums';
import { IngestionSummary, PaginatedResult } from '../domain/types';
import { hasNonTerminalIngestion } from '../utils/ingestion-status.utils';
import { isTabVisible } from '../utils/tab-visibility.util';
import { useAsyncResource } from './use-async-resource';
import { useCursorPagination } from './use-cursor-pagination';

/** Live pipelines are polled so operators see progress without refreshing. */
export const DEFAULT_POLL_INTERVAL_MS = 5000;

export interface UseIngestionsParams {
  status?: FileProcessingStatus;
  dataProvider?: string;
  createdAfter?: string;
  createdBefore?: string;
  limit?: number;
  /** Set to `0` to disable polling entirely (e.g. in tests). */
  pollIntervalMs?: number;
}

export interface UseIngestionsReturn {
  ingestions: IngestionSummary[];
  isLoading: boolean;
  hasError: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  currentPage: number;
  goToNext: () => void;
  goToPrevious: () => void;
  refetch: () => void;
}

const EMPTY_INGESTIONS_PAGE: PaginatedResult<IngestionSummary> = {
  items: [],
  pageInfo: { hasNextPage: false, nextCursor: null },
};

/**
 * Fetches a page of ingestions through the `PartnerDataClient` port and
 * tracks cursor history (see `use-cursor-pagination.ts`) so the caller can
 * page forward/backward without knowing anything about how cursors are
 * transported.
 *
 * PDGS exposes no total count, so forward paging is driven by
 * `pageInfo.hasNextPage` rather than by comparing against a total.
 *
 * While the current page still contains a non-terminal ingestion (and the
 * tab is visible) the hook polls the port so an operator watching a live
 * pipeline sees progress without refreshing.
 */
export const useIngestions = (
  params: UseIngestionsParams = {},
): UseIngestionsReturn => {
  const {
    status,
    dataProvider,
    createdAfter,
    createdBefore,
    limit = DEFAULT_PAGE_SIZE,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  } = params;
  const client = usePartnerDataClient();
  const { cursor, hasPrevious, currentPage, goToNext, goToPrevious, reset } =
    useCursorPagination();

  const fetchIngestions = useCallback(
    () =>
      client.listIngestions({
        cursor,
        limit,
        status,
        dataProvider,
        createdAfter,
        createdBefore,
      }),
    [client, cursor, limit, status, dataProvider, createdAfter, createdBefore],
  );

  const { data, isLoading, hasError, refetch } = useAsyncResource(
    fetchIngestions,
    EMPTY_INGESTIONS_PAGE,
  );

  // Reset to the first page whenever the filters (but not the cursor) change,
  // so a filtered result never starts partway through the previous result's
  // cursor stack.
  useEffect(() => {
    reset();
  }, [status, dataProvider, createdAfter, createdBefore, limit, reset]);

  // Poll via a ref so the interval always calls the latest `refetch` without
  // being torn down and recreated on every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const shouldPoll = hasNonTerminalIngestion(data.items);

  useEffect(() => {
    if (!shouldPoll || pollIntervalMs <= 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (isTabVisible()) {
        refetchRef.current();
      }
    }, pollIntervalMs);

    return (): void => clearInterval(intervalId);
  }, [shouldPoll, pollIntervalMs]);

  return {
    ingestions: data.items,
    isLoading,
    hasError,
    hasNext: data.pageInfo.hasNextPage,
    hasPrevious,
    currentPage,
    goToNext: (): void => goToNext(data.pageInfo),
    goToPrevious,
    refetch,
  };
};
